/**
 * API Manager API Client
 * 
 * Handles all HTTP requests to API Manager service
 */

import axios from 'axios';
import { cpFetch, getDefaultTenantId } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { resolveExposeExecuteTenant } from './exposeTenantHonesty';

const getAuthToken = () => {
  const auth = localStorage.getItem('dc.auth');
  return auth ? JSON.parse(auth).token : null;
};

/** Session tenant only. Never invent tenant=dev or stamp leftover dc.app_id. */
export function resolveApiManagerSessionTenant(): string | null {
  try {
    const selectedTenant = useAuthStore.getState()?.selectedTenant;
    const raw = localStorage.getItem('dc.auth');
    const auth = raw ? JSON.parse(raw) : {};
    return resolveExposeExecuteTenant({
      selectedTenant: selectedTenant || auth?.tenant || auth?.tenantId,
      hostTenant: getDefaultTenantId(),
    });
  } catch {
    return resolveExposeExecuteTenant({
      selectedTenant: null,
      hostTenant: getDefaultTenantId(),
    });
  }
}

export const apiManagerClient = axios.create({
  baseURL: window.location.origin
});

apiManagerClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  const tenant = resolveApiManagerSessionTenant();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (tenant && !config.headers['x-tenant-id']) config.headers['x-tenant-id'] = tenant;
  
  // Debug logging (only in development)
  if (import.meta.env.DEV || localStorage.getItem('dc.debug.api') === '1') {
    console.debug('[API Manager Client]', {
      url: config.url,
      method: config.method,
      headers: {
        hasAuth: !!token,
        tenant: tenant || 'MISSING',
        app_id: config.headers['x-app-id'] || 'none'
      }
    });
  }
  
  return config;
});

// Response interceptor for better error logging
apiManagerClient.interceptors.response.use(
  response => response,
  error => {
    if (import.meta.env.DEV || localStorage.getItem('dc.debug.api') === '1') {
      console.error('[API Manager Client Error]', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });
    }
    return Promise.reject(error);
  }
);

/**
 * api-manager guards some writes with requireInfraChangeSet (409
 * infra_changeset_required without x-infra-changeset-id). Same ops flow as
 * SecretsManagerPage: create -> proposed -> approved, then send the id.
 */
export async function createApprovedInfraChangeset(target: string, reason: string): Promise<string> {
  const created = (await cpFetch('/infra-changesets', {
    method: 'POST',
    body: JSON.stringify({
      title: `API Manager change: ${target}`,
      description: reason,
      target,
      operations: [{ op: target }],
    }),
  })) as { changeset?: { changeset_id?: string } };
  const id = String(created?.changeset?.changeset_id || '');
  if (!id) throw new Error('Failed to create infra changeset');
  for (const state of ['proposed', 'approved']) {
    await cpFetch(`/infra-changesets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ state }),
    });
  }
  return id;
}

async function withInfraChangeset(target: string, reason: string) {
  const id = await createApprovedInfraChangeset(target, reason);
  return { headers: { 'x-infra-changeset-id': id } };
}

// API Manager API methods
export const apiManagerApi = {
  // External APIs
  listApis: (params: { search?: string; status?: string; limit?: number } = {}) =>
    apiManagerClient.get('/cp/api-ingest/api/external-apis', { params }),
  getApi: (apiId: string) => apiManagerClient.get(`/cp/api-ingest/api/external-apis/${apiId}`),
  createApi: async (data: any) =>
    apiManagerClient.post('/cp/api-ingest/api/external-apis', data,
      await withInfraChangeset('api-ingest/external-apis:create', 'Create external API from API Manager')),
  updateApi: async (apiId: string, data: any) =>
    apiManagerClient.put(`/cp/api-ingest/api/external-apis/${apiId}`, data,
      await withInfraChangeset('api-ingest/external-apis:update', `Update external API ${apiId}`)),
  saveApiCredentials: (apiId: string, data: Record<string, unknown>) =>
    apiManagerClient.post(`/cp/api-ingest/api/external-apis/${encodeURIComponent(apiId)}/credentials`, data),
  saveApiHeaders: (apiId: string, data: { user_agent?: string }) =>
    apiManagerClient.post(`/cp/api-ingest/api/external-apis/${encodeURIComponent(apiId)}/headers`, data),
  deleteApi: (apiId: string) => apiManagerClient.delete(`/cp/api-ingest/api/external-apis/${apiId}`),
  getApiEndpoints: (apiId: string) => apiManagerClient.get(`/cp/api-ingest/api/external-apis/${apiId}/endpoints`),
  updateEndpointPath: (apiId: string, endpointId: string, data: { path?: string; url?: string }) =>
    apiManagerClient.patch(
      `/cp/api-ingest/api/external-apis/${encodeURIComponent(apiId)}/endpoints/${encodeURIComponent(endpointId)}`,
      data,
    ),
  deleteEndpoint: async (apiId: string, endpointId: string) =>
    apiManagerClient.delete(`/cp/api-ingest/api/external-apis/${apiId}/endpoints/${encodeURIComponent(endpointId)}`,
      await withInfraChangeset('api-ingest/external-apis:endpoint-delete', `Delete endpoint ${endpointId} on ${apiId}`)),
  listWorkspaceApis: (params: { search?: string; group?: string; status?: string; limit?: number; window_minutes?: number; app_id?: string; scope?: 'all' } = {}) =>
    apiManagerClient.get('/cp/api-ingest/workspace/apis', { params }),
  getWorkspaceApiDetail: (apiId: string, params: { history_limit?: number } = {}) =>
    apiManagerClient.get(`/cp/api-ingest/workspace/apis/${encodeURIComponent(apiId)}/detail`, { params }),
  updateWorkspaceMeta: (
    apiId: string,
    data: {
      api?: { api_name?: string; provider?: string; base_url?: string; status?: string; description?: string };
      metadata?: { group_override?: string | null; tags?: string[]; owner?: string | null; criticality?: string | null; notes?: string | null };
    }
  ) => apiManagerClient.patch(`/cp/api-ingest/workspace/apis/${encodeURIComponent(apiId)}/meta`, data),

  // App-level BYOK connections (per app_id)
  listAppConnections: (params: { api_id?: string; app_id?: string } = {}) =>
    apiManagerClient.get('/cp/api-ingest/api/app-connections', { params }),
  listAppConnectionPanel: (appId: string) =>
    apiManagerClient.get('/cp/api-ingest/api/app-connections/panel', {
      headers: { 'x-app-id': appId },
      params: { app_id: appId },
    }),
  upsertAppConnection: (body: { api_id: string; credentials: any; auth_type?: string; status?: string }) =>
    apiManagerClient.post('/cp/api-ingest/api/app-connections', body),
  deleteAppConnection: (connectionId: string) =>
    apiManagerClient.delete(`/cp/api-ingest/api/app-connections/${encodeURIComponent(connectionId)}`),

  // Tenant shared access grants (admin controls who can use external_apis.credentials)
  listSharedAccessGrants: (params: { api_id?: string; app_id?: string; status?: string } = {}) =>
    apiManagerClient.get('/cp/api-ingest/api/shared-access/grants', { params }),
  grantSharedAccess: (body: { api_id: string; app_id: string; reason?: string; expires_at?: string | null; limits?: { rpm?: number; rpd?: number } }) =>
    apiManagerClient.post('/cp/api-ingest/api/shared-access/grant', body),
  revokeSharedAccess: (body: { api_id: string; app_id: string; reason?: string }) =>
    apiManagerClient.post('/cp/api-ingest/api/shared-access/revoke', body),
  
  // API Connections
  listConnections: (apiId?: string) => apiManagerClient.get('/cp/api-ingest/api/connections', {
    params: apiId ? { api_id: apiId } : {}
  }),
  getConnection: (connectionId: string) => apiManagerClient.get(`/cp/api-ingest/api/connections/${connectionId}`),
  createConnection: (data: any) => apiManagerClient.post('/cp/api-ingest/api/connections', data),
  deleteConnection: (connectionId: string) => apiManagerClient.delete(`/cp/api-ingest/api/connections/${connectionId}`),
  testConnection: (connectionId: string) => apiManagerClient.post(`/cp/api-ingest/api/connections/${connectionId}/test`),
  
  // Field Mappings
  listMappings: (apiId?: string, endpointId?: string) => apiManagerClient.get('/cp/api-ingest/api/mappings', {
    params: { api_id: apiId, endpoint_id: endpointId }
  }),
  getMapping: (mappingId: string) => apiManagerClient.get(`/cp/api-ingest/api/mappings/${mappingId}`),
  createMapping: async (data: any) =>
    apiManagerClient.post('/cp/api-ingest/api/mappings', data,
      await withInfraChangeset('api-ingest/field-mappings:create', 'Create field mapping from API Manager')),
  updateMapping: async (mappingId: string, data: any) =>
    apiManagerClient.put(`/cp/api-ingest/api/mappings/${mappingId}`, data,
      await withInfraChangeset('api-ingest/field-mappings:update', `Update field mapping ${mappingId}`)),
  deleteMapping: async (mappingId: string) =>
    apiManagerClient.delete(`/cp/api-ingest/api/mappings/${mappingId}`,
      await withInfraChangeset('api-ingest/field-mappings:delete', `Delete field mapping ${mappingId}`)),
  confirmMapping: async (mappingId: string) =>
    apiManagerClient.post(`/cp/api-ingest/api/mappings/${mappingId}/confirm`, {},
      await withInfraChangeset('api-ingest/field-mappings:confirm', `Confirm field mapping ${mappingId}`)),
  
  // API Consumers
  listConsumers: (status?: string) => apiManagerClient.get('/cp/api-expose/api/consumers', {
    params: status ? { status } : {}
  }),
  getConsumer: (consumerId: string) => apiManagerClient.get(`/cp/api-expose/api/consumers/${consumerId}`),
  createConsumer: (data: any) => apiManagerClient.post('/cp/api-expose/api/consumers', data),
  updateConsumer: (consumerId: string, data: any) => apiManagerClient.put(`/cp/api-expose/api/consumers/${consumerId}`, data),
  deleteConsumer: (consumerId: string) => apiManagerClient.delete(`/cp/api-expose/api/consumers/${consumerId}`),
  regenerateApiKey: (consumerId: string) => apiManagerClient.post(`/cp/api-expose/api/consumers/${consumerId}/regenerate-key`),
  getConsumerUsage: (consumerId: string) => apiManagerClient.get(`/cp/api-expose/api/consumers/${consumerId}/usage`),
  listMcpEndpoints: () => apiManagerClient.get('/cp/api-expose/mcp/endpoints'),
  listDataSources: (appId: string) => apiManagerClient.get(`/cp/api-expose/data-sources/${encodeURIComponent(appId)}`),
  upsertDataSource: (
    appId: string,
    body: {
      source_type: string;
      source_id?: string | null;
      policy_ref?: { kind?: string | null; id?: string | null };
      scopes?: string[];
      filters?: Record<string, any>;
      rate_limit?: { rpm?: number | null } | null;
      enabled?: boolean;
    }
  ) => apiManagerClient.put(`/cp/api-expose/data-sources/${encodeURIComponent(appId)}/by-source`, body),
  updateDataSource: (
    appId: string,
    bindingId: string,
    body: {
      source_type?: string;
      source_id?: string | null;
      policy_ref?: { kind?: string | null; id?: string | null };
      scopes?: string[];
      filters?: Record<string, any>;
      rate_limit?: { rpm?: number | null } | null;
      enabled?: boolean;
    }
  ) => apiManagerClient.patch(`/cp/api-expose/data-sources/${encodeURIComponent(appId)}/${encodeURIComponent(bindingId)}`, body),
  deleteDataSource: (appId: string, bindingId: string) =>
    apiManagerClient.delete(`/cp/api-expose/data-sources/${encodeURIComponent(appId)}/${encodeURIComponent(bindingId)}`),
  
  // Agent Tools (AI-Powered)
  registerExternalApi: (formData: FormData) => apiManagerClient.post('/cp/api-ingest/agent-tools/register-external-api', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  configureApiAuth: (data: any) => apiManagerClient.post('/cp/api-ingest/agent-tools/configure-api-auth', data),
  testApiIntegration: (data: any) => apiManagerClient.post('/cp/api-ingest/agent-tools/test-api-integration', data),
  
  // Health Check
  health: () => apiManagerClient.get('/cp/api-ingest/health'),

  // Ingest ops (history/status/replay)
  ingestHistory: (params: { limit?: number; offset?: number; provider_key?: string; api_id?: string; status?: string; app_id?: string } = {}) =>
    apiManagerClient.get('/cp/api-ingest/api/ingest/history', { params }),
  ingestStatus: (params: { window_minutes?: number; app_id?: string } = {}) =>
    apiManagerClient.get('/cp/api-ingest/api/ingest/status', { params }),
  ingestOverview: (params: { window_minutes?: number; api_id?: string; app_id?: string } = {}) =>
    apiManagerClient.get('/cp/api-ingest/api/ingest/overview', { params }),
  replayIngest: (ingestId: string) =>
    apiManagerClient.post(`/cp/api-ingest/api/ingest/replay/${encodeURIComponent(ingestId)}`, { confirm: true }),
  ingestHistoryItem: (ingestId: string, params: { include_envelope?: boolean } = {}) =>
    apiManagerClient.get(`/cp/api-ingest/api/ingest/history/${encodeURIComponent(ingestId)}`, {
      params: { include_envelope: params.include_envelope ? 1 : 0 },
    }),

  // Connector catalog (prebuilt provider connectors: install, scheduled sync)
  listConnectorCatalog: () => apiManagerClient.get('/cp/api-ingest/api/connectors/catalog'),
  installConnector: async (providerKey: string) =>
    apiManagerClient.post('/cp/api-ingest/api/connectors/install', { provider_key: providerKey },
      await withInfraChangeset('api-ingest/connectors:install', `Install connector ${providerKey}`)),
  setConnectorSchedule: (apiId: string, body: { enabled: boolean; every_seconds?: number }) =>
    apiManagerClient.post(`/cp/api-ingest/api/connectors/${encodeURIComponent(apiId)}/schedule`, body),

  // Pipelines
  listPipelines: (params: { api_id?: string; endpoint_id?: string; enabled?: boolean } = {}) =>
    apiManagerClient.get('/cp/api-ingest/api/pipelines', { params }),
  runPipeline: (pipelineId: string, body: { run_kind?: 'health_ping' | 'instance_ingest' } = {}) =>
    apiManagerClient.post(`/cp/api-ingest/api/pipelines/${encodeURIComponent(pipelineId)}/run`, body),
  updatePipeline: async (
    pipelineId: string,
    body: { enabled?: boolean; schedule?: { every_seconds: number }; store_mode?: string },
  ) =>
    apiManagerClient.put(`/cp/api-ingest/api/pipelines/${encodeURIComponent(pipelineId)}`, body,
      await withInfraChangeset('api-ingest/pipelines:update', `Update pipeline ${pipelineId} from API Ingest`)),
  updatePipelineQuery: (pipelineId: string, body: { query: Record<string, string | number>; enabled?: boolean }) =>
    apiManagerClient.patch(`/cp/api-ingest/api/pipelines/${encodeURIComponent(pipelineId)}/query`, body),
  runPipelinePlayground: (pipelineId: string) =>
    apiManagerClient.post(`/cp/api-ingest/api/pipelines/${encodeURIComponent(pipelineId)}/playground-run`, {}),

  // One-shot ingest (write to Timescale)
  ingestEndpoint: (apiId: string, endpointId: string, body: any = {}) =>
    apiManagerClient.post(`/cp/api-ingest/api/external-apis/${apiId}/ingest`, {
      endpoint_id: endpointId,
      ...body,
    }),
  
  // Playground (quick testing)
  quickRegisterApi: (formData: FormData) => apiManagerClient.post('/cp/api-ingest/playground/quick-register', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  quickTestEndpoint: (apiId: string, endpointId: string, params: any) => 
    apiManagerClient.post('/cp/api-ingest/playground/test-endpoint', { 
      apiId, 
      endpointId, 
      request: { query: params || {} }
    }),
  previewTransformation: (apiId: string, endpointId: string, sampleData: any) =>
    apiManagerClient.post('/cp/api-ingest/playground/preview-transform', { apiId, endpointId, sampleData }),
  getRecentTests: () => apiManagerClient.get('/cp/api-ingest/playground/recent-tests')
};

export const apiOAuthApi = {
  listProviders: () => apiManagerClient.get('/cp/api-ingest/api/oauth/providers'),
  start: (body: {
    api_id: string;
    provider: string;
    client_id: string;
    client_secret: string;
    authorization_url?: string;
    token_url?: string;
    grant_type?: 'authorization_code' | 'client_credentials';
    api_key?: string;
    x_api_key?: string;
    scopes?: string[];
    redirect_uri?: string;
  }) => apiManagerClient.post('/cp/api-ingest/api/oauth/start', body),
};

export async function mintAppToken(params: { tenantId: string; apiKey: string }) {
  const { tenantId, apiKey } = params;
  if (!tenantId) throw new Error('Missing tenantId');
  if (!apiKey) throw new Error('Missing apiKey');
  const res = await apiManagerClient.post(
    '/cp/api-expose/auth/app-token',
    {},
    {
      headers: {
        'x-tenant-id': tenantId,
        'x-api-key': apiKey
      }
    }
  );
  const token = res?.data?.token;
  if (!token) throw new Error('Token minting failed (no token returned)');
  return res.data as { token: string; expires_in?: number; token_type?: string };
}

export default apiManagerApi;

